import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { removeFormField, selectAddressFormData, setFormField } from 'slices/addressFormSlice'
import { useAppDispatch } from 'store/hooks'
import {
  StyledCol,
  StyledColContainer,
  StyledRowContainer,
} from './ValueModernizationMatrixForm.styled'
import MultiSelect from 'components/MultiSelect/MultiSelect'
import { ModernizationLevelType } from 'types/ApiResponse'

const ValueModernizationMatrixForm = (props: any) => {
  const { showValidation, validationErrors, setValidationErrors } = props
  const dispatch = useAppDispatch()
  const addressFormData = useSelector(selectAddressFormData)
  const { t } = useTranslation()

  const modernizationOptions = [
    {
      value: ModernizationLevelType.NONE,
      label: t(`modernizationLevel.${ModernizationLevelType.NONE.toLowerCase()}`),
    },
    {
      value: ModernizationLevelType.PARTIAL,
      label: t(`modernizationLevel.${ModernizationLevelType.PARTIAL.toLowerCase()}`),
    },
    {
      value: ModernizationLevelType.FULL,
      label: t(`modernizationLevel.${ModernizationLevelType.FULL.toLowerCase()}`),
    },
  ]

  const validate = (name, property) => {
    if (property === null || property === undefined || property === '') {
      if (!validationErrors.includes(name)) {
        setValidationErrors((errors) => [name, ...errors])
      }
      return showValidation && true
    } else {
      if (validationErrors.includes(name)) {
        setValidationErrors((errors) => errors.filter((error) => error !== name))
      }
      return false
    }
  }

  const validationMultiSelect = (value, options, formFieldName) => {
    if (!options) {
      return value
    }
    const matchingOption = options.find(
      (option) => option.value === `${value}` || option.key === `${value}`
    )
    if (
      (value !== null && value !== undefined && !matchingOption) ||
      value === null ||
      value === undefined
    ) {
      dispatch(removeFormField(formFieldName))
      return false
    }
    return true
  }

  const onChange = (value: any, key: string) => {
    dispatch(setFormField({ key: key, value: value }))
  }

  return (
    <>
      <StyledRowContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'interiorDesign',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix?.interiorDesign
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(event.target.value, 'propertyDetails.modernizationMatrix.interiorDesign')
              }
              label={`${t('newAddressModal.modernizationTab.interiorDesign.label')}*`}
              value={
                addressFormData.addressData?.propertyDetails?.modernizationMatrix?.interiorDesign
              }
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.interiorDesign"
            />
          </StyledCol>
        </StyledColContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'sanitaryArea',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix?.sanitaryArea
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(event.target.value, 'propertyDetails.modernizationMatrix.sanitaryArea')
              }
              label={`${t('newAddressModal.modernizationTab.sanitaryArea.label')}*`}
              value={
                addressFormData.addressData?.propertyDetails?.modernizationMatrix?.sanitaryArea
              }
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.sanitaryArea"
            />
          </StyledCol>
        </StyledColContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'heatingSystem',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix?.heatingSystem
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(event.target.value, 'propertyDetails.modernizationMatrix.heatingSystem')
              }
              label={`${t('newAddressModal.modernizationTab.heatingSystem.label')}*`}
              value={
                addressFormData.addressData?.propertyDetails?.modernizationMatrix?.heatingSystem
              }
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.heatingSystem"
            />
          </StyledCol>
        </StyledColContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'pipes',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix?.pipes
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(event.target.value, 'propertyDetails.modernizationMatrix.pipes')
              }
              label={`${t('newAddressModal.modernizationTab.pipes.label')}*`}
              value={addressFormData.addressData?.propertyDetails?.modernizationMatrix?.pipes}
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.pipes"
            />
          </StyledCol>
        </StyledColContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'windows',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix?.windows
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(event.target.value, 'propertyDetails.modernizationMatrix.windows')
              }
              label={`${t('newAddressModal.modernizationTab.windows.label')}*`}
              value={addressFormData.addressData?.propertyDetails?.modernizationMatrix?.windows}
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.windows"
            />
          </StyledCol>
        </StyledColContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'thermalInsulation',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix?.thermalInsulation
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(
                  event.target.value,
                  'propertyDetails.modernizationMatrix.thermalInsulation'
                )
              }
              label={`${t('newAddressModal.modernizationTab.thermalInsulation.label')}*`}
              value={
                addressFormData.addressData?.propertyDetails?.modernizationMatrix?.thermalInsulation
              }
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.thermalInsulation"
            />
          </StyledCol>
        </StyledColContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'roofCoveringAndInsulation',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix
                ?.roofCoveringAndInsulation
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(
                  event.target.value,
                  'propertyDetails.modernizationMatrix.roofCoveringAndInsulation'
                )
              }
              label={`${t('newAddressModal.modernizationTab.roofCoveringAndInsulation.label')}*`}
              value={
                addressFormData.addressData?.propertyDetails?.modernizationMatrix
                  ?.roofCoveringAndInsulation
              }
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.roofCoveringAndInsulation"
            />
          </StyledCol>
        </StyledColContainer>
        <StyledColContainer>
          <StyledCol
            validationError={validate(
              'roomLayout',
              addressFormData.addressData?.propertyDetails?.modernizationMatrix?.roomLayout
            )}
          >
            <MultiSelect
              options={modernizationOptions}
              onChange={(event) =>
                onChange(event.target.value, 'propertyDetails.modernizationMatrix.roomLayout')
              }
              label={`${t('newAddressModal.modernizationTab.roomLayout.label')}*`}
              value={addressFormData.addressData?.propertyDetails?.modernizationMatrix?.roomLayout}
              validation={validationMultiSelect}
              formFieldName="propertyDetails.modernizationMatrix.roomLayout"
            />
          </StyledCol>
        </StyledColContainer>
      </StyledRowContainer>
    </>
  )
}

export default ValueModernizationMatrixForm
